import { useAuth } from '../context/AuthContext';
import { Crown } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const AFFILIATE_URL = 'https://airhelp.tpx.gr/qaSrWHll';

// Header Banner - Combined AirHelp + Premium upgrade for logged-in free users
export const HeaderAffiliateBanner = () => {
    const { user, isAuthenticated } = useAuth();
    const navigate = useNavigate();
    
    // Hide for premium users
    const isPremium = user?.is_premium === true;
    if (isPremium) return null;
    
    const isLoggedInFree = isAuthenticated && !isPremium;
    
    return (
        <div 
            className="w-full mt-16"
            style={{ 
                background: 'linear-gradient(135deg, #0f4c81 0%, #1a5d9e 50%, #2570b5 100%)',
            }}
            data-testid="header-affiliate-banner"
        >
            <div className="max-w-7xl mx-auto px-4 py-2.5 flex items-center justify-between gap-4">
                {/* Left: AirHelp Affiliate Link */}
                <a 
                    href={AFFILIATE_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 flex-1 min-w-0 hover:opacity-90 transition-opacity"
                >
                    {/* AirHelp Logo Icon */}
                    <div className="flex-shrink-0 w-9 h-9 bg-white/10 rounded-lg flex items-center justify-center">
                        <svg viewBox="0 0 100 100" className="w-5 h-5">
                            <path d="M25 80 L50 20 L75 80 Z" fill="#3b82f6"/>
                            <path d="M40 85 L65 25 L90 85 Z" fill="#f87171" opacity="0.85"/>
                        </svg>
                    </div>
                    
                    <div className="min-w-0">
                        <p className="text-white font-semibold text-sm truncate">
                            Flight Delayed? Get up to €600
                        </p>
                        <div className="hidden sm:flex items-center gap-2 text-xs text-blue-200">
                            <span className="flex items-center gap-1">
                                <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
                                </svg>
                                No Win, No Fee
                            </span>
                            <span>·</span>
                            <span>Check Now →</span>
                        </div>
                    </div>
                </a>
                
                {/* Divider - only show if logged in */}
                {isLoggedInFree && (
                    <div className="hidden md:block h-8 w-px bg-white/20"></div>
                )}
                
                {/* Right: Premium Upgrade CTA (only for logged-in free users) */}
                {isLoggedInFree ? (
                    <button
                        onClick={() => navigate('/profile')}
                        className="flex-shrink-0 flex items-center gap-2 px-4 py-2 bg-[#f07050] hover:bg-[#e06040] text-white font-semibold text-sm rounded-lg transition-colors shadow-lg"
                        data-testid="header-upgrade-btn"
                    >
                        <Crown className="h-4 w-4" />
                        <span className="hidden sm:inline">Go Premium</span>
                        <span className="sm:hidden">Upgrade</span>
                    </button>
                ) : (
                    /* Public users see AirHelp CTA button */
                    <a 
                        href={AFFILIATE_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex-shrink-0"
                    >
                        <span className="inline-flex items-center px-4 py-2 bg-white text-blue-700 font-semibold text-sm rounded-lg hover:bg-blue-50 transition-colors shadow-lg">
                            Check Now
                            <svg className="ml-1.5 w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                            </svg>
                        </span>
                    </a>
                )}
            </div>
        </div>
    );
};

// Footer Banner - Combined AirHelp + Premium upgrade
export const FooterAffiliateBanner = () => {
    const { user, isAuthenticated } = useAuth();
    const navigate = useNavigate();
    
    // Hide for premium users
    if (user?.is_premium) return null;
    
    const isLoggedInFree = isAuthenticated && !user?.is_premium;
    
    return (
        <div 
            className="w-full bg-[#0f172a] border-t border-slate-800"
            data-testid="footer-affiliate-banner"
        >
            <div className="max-w-7xl mx-auto px-4 py-2.5 flex items-center justify-between gap-4">
                {/* Left: AirHelp */}
                <a 
                    href={AFFILIATE_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 hover:opacity-90 transition-opacity"
                >
                    <div className="flex-shrink-0 w-8 h-8 relative">
                        <svg viewBox="0 0 100 100" className="w-full h-full">
                            <path d="M25 85 L50 15 L75 85 Z" fill="#3b82f6"/>
                            <path d="M40 90 L65 20 L90 90 Z" fill="#f87171" opacity="0.9"/>
                        </svg>
                    </div>
                    <div>
                        <p className="text-white font-semibold text-sm">AirHelp</p>
                        <p className="text-slate-400 text-xs hidden sm:block">Flight compensation</p>
                    </div>
                </a>
                
                {/* Center - Message */}
                <a 
                    href={AFFILIATE_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hidden md:block flex-1 text-center hover:opacity-90 transition-opacity"
                >
                    <p className="text-slate-300 text-sm">
                        Flight delayed? Get up to <span className="text-green-400 font-semibold">€600</span> compensation →
                    </p>
                </a>
                
                {/* Right: Premium upgrade for logged-in, or AirHelp CTA for public */}
                {isLoggedInFree ? (
                    <button
                        onClick={() => navigate('/profile')}
                        className="flex-shrink-0 flex items-center gap-2 px-4 py-2 bg-[#f07050] hover:bg-[#e06040] text-white font-semibold text-sm rounded-lg transition-colors"
                        data-testid="footer-upgrade-btn"
                    >
                        <Crown className="h-4 w-4" />
                        <span className="hidden sm:inline">Remove Ads</span>
                        <span className="sm:hidden">Premium</span>
                    </button>
                ) : (
                    <a 
                        href={AFFILIATE_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex-shrink-0"
                    >
                        <span className="inline-flex items-center px-4 py-2 bg-blue-600 text-white font-semibold text-sm rounded-lg hover:bg-blue-500 transition-colors">
                            Check eligibility
                        </span>
                    </a>
                )}
            </div>
        </div>
    );
};
